import { ChangeEvent, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { SaleCenterClosingSummary } from "../../types/sale-center-closing.type";
import { formatNumber } from "../../utils";
import AmountInput from "./AmountInput";
import ClosingSectionBox from "./ClosingSectionBox";
import ClosingSummaryLine from "./ClosingSummaryLine";
import {
  CASH_COLLECTED_LABEL,
  EXPENSE_LABEL,
  SEND_BACK_LABEL,
  closingExpenseAmount,
} from "./saleCenterClosing.utils";
import { parseNonNegativeMoney } from "./transfer.utils";

/** The figure is counted against Express, so an empty field must not look like a keyed 0. */
const CASH_COLLECTED_PLACEHOLDER = "ระบุยอดเงินสดที่นับได้";

/** Why every control is inert, for the cohort that reads the box but may not act on it. */
const READ_ONLY_HINT =
  "เฉพาะหัวหน้าแผนกบัญชีสำนักงานใหญ่เท่านั้นที่ปิดศูนย์เซลหรือส่งกลับให้สาขาแก้ไขได้";

interface ClosingActionBoxProps {
  summary: SaleCenterClosingSummary;
  /**
   * Whether the signed-in user may actually close or send back. False leaves the box on
   * screen with every control inert — see the component note.
   */
  canAct: boolean;
  /** True while a close or send-back is in flight. */
  saving: boolean;
  onConfirm: (cashCollectedAmount: number, note: string | null) => void;
  onSendBack: () => void;
}

/**
 * The ปิดศูนย์เซล form: the cash figure, the note, and the two decisions — close the
 * branch-day, or hand it back to the branch.
 *
 * Rendered only while the branch-day can still be closed. A frozen day has nothing left to key
 * in, so the panel puts the recorded outcome in this column instead and moves the one control
 * that survives a closing, ส่งกลับให้สาขาแก้ไข, into its header.
 *
 * Only the head-office accounting supervisor may submit either decision; the server accepts
 * them from nobody else. The rest of the review cohort — the branch back office and the plain
 * head-office accountant — still see the box, exactly as she does, with every control disabled
 * and a line saying whose decision this is. That is the customer's spec: the branch is told
 * what is about to be decided about its day and by whom, rather than being shown a panel that
 * ends where the decision starts.
 *
 * There is deliberately no draft. Nothing typed here is persisted until ยืนยันปิดศูนย์เซล, so
 * ยกเลิก simply discards what was typed; leaving the screen loses it too, which is correct for
 * a figure that is only meaningful at the moment it is signed.
 */
export default function ClosingActionBox({
  summary,
  canAct,
  saving,
  onConfirm,
  onSendBack,
}: ClosingActionBoxProps) {
  const [cashInput, setCashInput] = useState<string>("");
  const [note, setNote] = useState<string>("");

  // Nothing typed here is a draft, and the branch-day being handed back to the branch under
  // the supervisor is a new decision, not a continuation of the one being typed: a figure
  // keyed before the send-back would stay in the field looking freshly counted.
  useEffect(() => {
    setCashInput("");
    setNote("");
  }, [summary.status]);

  // Blank is "not keyed yet", not zero: a supervisor who has typed nothing has made no
  // statement about the cash, while a typed 0 is a real figure and stays submittable.
  const hasCashEntry = cashInput.trim() !== "";
  const cashCollectedAmount = hasCashEntry
    ? parseNonNegativeMoney(cashInput)
    : null;
  const previewExpenseAmount =
    cashCollectedAmount == null
      ? null
      : closingExpenseAmount(cashCollectedAmount, summary.required_transfer_total);

  const isBlocked = !summary.can_close;
  const inputsDisabled = !canAct || saving;
  const canSubmit = canAct && !saving && !isBlocked && cashCollectedAmount != null;

  const handleChangeNote = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleDiscardDraft = () => {
    setCashInput("");
    setNote("");
  };

  const handleConfirm = () => {
    if (cashCollectedAmount == null) return;
    onConfirm(cashCollectedAmount, note.trim() ? note.trim() : null);
  };

  return (
    <ClosingSectionBox
      title="ปิดศูนย์เซล"
      icon={LockOutlinedIcon}
      accent="error"
    >
      {/* A dead form with no explanation was the first thing the branch objected to, so the
          reason for it is stated before anything the reader might try to press. */}
      {!canAct && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {READ_ONLY_HINT}
        </Typography>
      )}

      <Stack gap={2}>
        <AmountInput
          label={CASH_COLLECTED_LABEL}
          placeholder={CASH_COLLECTED_PLACEHOLDER}
          value={cashInput}
          disabled={inputsDisabled}
          onChange={setCashInput}
        />
        {/* The last number checked before signing: it moves with every keystroke so the
            supervisor reconciles against Express here rather than on a calculator. It
            appears with the cash figure it is derived from — on its own it would be a
            labelled dash the supervisor has to work out the meaning of. */}
        {previewExpenseAmount != null && (
          <ClosingSummaryLine
            label={EXPENSE_LABEL}
            value={formatNumber(previewExpenseAmount)}
          />
        )}
        <TextField
          label="หมายเหตุ (ถ้ามี)"
          value={note}
          onChange={handleChangeNote}
          size="small"
          fullWidth
          multiline
          minRows={3}
          disabled={inputsDisabled}
        />
      </Stack>

      {/* Wraps rather than overflowing: the box is half the panel's width from `md` up, and
          three Thai button labels on one line are wider than that. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mt={2}
      >
        {/* A day can be wrong long before anyone has decided it is finished, so it can be
            handed back without ever having been closed. */}
        <Button
          color="error"
          variant="outlined"
          disabled={inputsDisabled}
          onClick={onSendBack}
        >
          {SEND_BACK_LABEL}
        </Button>
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button
            color="inherit"
            disabled={inputsDisabled}
            onClick={handleDiscardDraft}
          >
            ยกเลิก
          </Button>
          {/* Greyed out with nothing to hover: the ผู้ตรวจสอบ timeline beside it already
              names every shortfall a reader can reach — each side's outstanding routes, the
              routes that have not remitted, and a day with no deposits yet. */}
          <Button
            variant="contained"
            color="error"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            ยืนยันปิดศูนย์เซล
          </Button>
        </Stack>
      </Stack>
    </ClosingSectionBox>
  );
}
